import React, { useState, useEffect, useRef } from "react";
import { db, storage } from "../firebase";
import { useNavigate, useParams } from "react-router-dom";
import { getDownloadURL, ref, uploadBytesResumable, deleteObject } from "firebase/storage";
import {
  addDoc,
  collection,
  getDoc,
  serverTimestamp,
  doc,
  updateDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { toast } from "react-toastify";
import { sendNewBlogNotification } from "../utils/notifications";
import "./AddEditBlog.css";
import Spinner from "../components/Spinner";
import heic2any from "heic2any"; // Import heic2any library for HEIC conversion
import ReactQuill from 'react-quill'; // Import React-Quill
import 'react-quill/dist/quill.snow.css'; // Import Quill styles

// Available emoji tags
const EMOJI_OPTIONS = [
  { emoji: '💻', label: 'Technology' },
  { emoji: '🎨', label: 'Art' },
  { emoji: '🍔', label: 'Food' },
  { emoji: '✈️', label: 'Travel' },
  { emoji: '📚', label: 'Education' },
  { emoji: '🏋️‍♂️', label: 'Fitness' },
  { emoji: '🎮', label: 'Gaming' },
  { emoji: '💼', label: 'Business' }
];

const initialState = {
  title: "",
  description: "",
  imgUrl: "",
  date: new Date().toISOString().split('T')[0], // Default to today's date
  emojiTag: "",
  pinned: false
}

// Custom handler for YouTube videos
const videoHandler = function() {
  const quill = this.quill;
  const range = quill.getSelection();
  
  const value = prompt("Please enter the YouTube video URL (e.g., https://www.youtube.com/watch?v=VIDEO_ID, https://youtu.be/VIDEO_ID, or https://youtube.com/shorts/VIDEO_ID)");
  
  if (value) {
    // Extract video ID from various YouTube URL formats including Shorts
    let videoId = '';
    
    // Support regular YouTube, youtu.be, and YouTube Shorts
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=|shorts\/)([^#&?]*).*/;
    const match = value.match(regExp);
    
    if (match && match[2].length === 11) {
      videoId = match[2];
      
      // Create responsive embed container for the video - centered by default
      const embedHtml = `
        <div class="video-embed-container" style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; max-width: 100%; margin: 20px auto; width: 90%;">
          <iframe 
            src="https://www.youtube.com/embed/${videoId}" 
            style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;" 
            frameborder="0" 
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
            allowfullscreen>
          </iframe>
        </div>
      `;
      
      // Insert the HTML at cursor position
      quill.clipboard.dangerouslyPasteHTML(range.index, embedHtml);
      
      // Move cursor after the inserted content
      quill.setSelection(range.index + 1);
      
      toast.success("YouTube video embedded successfully");
    } else {
      toast.error("Invalid YouTube URL. Please enter a valid YouTube video URL.");
    }
  }
};

// Custom link handler to fix Quill link button
const linkHandler = function() {
  const quill = this.quill;
  const range = quill.getSelection();
  
  if (range == null) return;
  
  let preview = quill.getText(range);
  if (/^\S+@\S+\.\S+$/.test(preview)) {
    preview = 'mailto:' + preview;
  } else if (!/^https?:\/\//.test(preview)) {
    // If it doesn't start with http:// or https://, assume it's a website and add https://
    if (preview && !preview.trim().startsWith('http')) {
      preview = 'https://' + preview;
    }
  }
  
  const value = prompt('Enter link URL:', preview || 'https://');
  if (value) {
    quill.format('link', value);
  } else {
    quill.format('link', false);
  }
};

// New approach with a function to create Quill modules to ensure proper loading in production
const createQuillModules = () => {
  // Create toolbar container with all buttons explicitly defined
  const container = [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    ['link'], // Removed 'image' to avoid duplicate image button
    ['video'], // YouTube video button
    ['clean']
  ];
  
  // Create handlers object with both custom handlers
  const handlers = {
    video: videoHandler,
    link: linkHandler
  };
  
  // Return the complete modules configuration
  return {
    toolbar: {
      container,
      handlers
    },
    clipboard: {
      matchVisual: false,
    }
  };
};

// Use the function to create modules
const quillModules = createQuillModules();

// Updated with all necessary formats to ensure buttons appear in production
const quillFormats = [
  'header',
  'bold', 'italic', 'underline',
  'list', 'bullet', 'ordered',
  'link',
  'image', 'video',
  'clean'
];

const AddEditBlog = ({user, setActive}) => {
  const navigate = useNavigate()
  const [form, setForm] = useState(initialState)
  const [file, setFile] = useState(null)
  const [progress, setProgress] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [showImagePopup, setShowImagePopup] = useState(false)
  const [imageUrl, setImageUrl] = useState("")
  const [imageAlignment, setImageAlignment] = useState("center")
  const {id} = useParams()
  const quillRef = useRef(null)
  const fileInputRef = useRef(null)

  const {title, description, date, emojiTag, pinned} = form

  useEffect(() => {
    const loadBlogData = async () => {
      if (id) {
        await getBlogDetail();
      }
      setIsLoading(false);
    };
    loadBlogData();
  }, [id]);

  useEffect(() => {
    const uploadFile = () => {
      if (!file) return;

      console.log("Uploading to Cloudinary:", file.name, "Size:", file.size);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_preset", "ujvaltech");

      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const percent = (e.loaded / e.total) * 100;
          setProgress(percent);
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200) {
          const data = JSON.parse(xhr.responseText);
          const downloadUrl = data.secure_url;
          console.log("Cloudinary upload successful:", downloadUrl);

          toast.success("Image uploaded successfully");
          insertImage(downloadUrl, imageAlignment);

          const updatedContent = quillRef.current?.getEditor().root.innerHTML || description;
          setForm(prev => ({ ...prev, description: updatedContent }));

          setProgress(null);
          setShowImagePopup(false);
          setFile(null);
        } else {
          console.error("Cloudinary upload failed:", xhr.responseText);
          toast.error("Image upload failed. Please try again.");
          setProgress(null);
          setFile(null);
        }
      };

      xhr.onerror = () => {
        console.error("Network error during upload");
        toast.error("Network error. Please check your connection and try again.");
        setProgress(null);
        setFile(null);
      };

      xhr.open("POST", "https://api.cloudinary.com/v1_1/dypl0jw6q/image/upload");
      xhr.send(formData);
    };

    file && uploadFile();
  }, [file, imageAlignment]);

  const getBlogDetail = async () => {
    try {
      const docRef = doc(db, "blogs", id);
      const snapshot = await getDoc(docRef);
      if (snapshot.exists()) {
        setForm({...snapshot.data()});
      }
      setActive(null);
    } catch (error) {
      toast.error("Error loading blog: " + error.message);
    }
  };

  const handleChange = (e) => {
    setForm({...form, [e.target.name]: e.target.value})
  }

  // Handle Quill editor content change
  const handleEditorChange = (content) => {
    console.log("Editor content changed:", content.substring(0, 50) + "...");
    if (content !== description) {
      setForm({...form, description: content});
    }
  }

  const handleEmojiSelect = (emoji) => {
    setForm({...form, emojiTag: emoji});
    setShowEmojiPicker(false);
  }

  const togglePin = () => {
    setForm({...form, pinned: !pinned});
    toast.info(pinned ? "Post unpinned" : "Post pinned - will appear at the top");
  }

  const validateForm = () => {
    if (!title.trim()) {
      toast.error("Title is required")
      return false
    }
    if (!description.trim() || description === '<p><br></p>') {
      toast.error("Description is required")
      return false
    }
    if (!date) {
      toast.error("Date is required")
      return false
    }
    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
   
    if (!title || !description) {
      return toast.error("All fields are mandatory");
    }
    
    if (description.length < 100) {
      return toast.error("Description should be at least 100 characters long");
    }
    
    setIsSubmitting(true);
    
    try {
      // Get the content from Quill editor
      const content = quillRef.current?.getEditor().root.innerHTML || description;
      
      // Parse the date to ensure consistent format
      const selectedDate = new Date(date);
      // Set time to noon to avoid timezone issues
      selectedDate.setHours(12, 0, 0, 0);
      
      // Calculate sort timestamp - CRITICAL for proper sorting
      const sortTimestampValue = selectedDate.getTime();
      console.log(`Blog date: ${date}, sortTimestamp: ${sortTimestampValue}`);
      
      // Prepare blog data
      const blogData = {
        title,
        description: content,
        emojiTag,
        date,
        pinned,
        author: user.displayName,
        userId: user.uid,
        likes: [],
        comments: [],
        // CRITICAL: Always include sortTimestamp for consistent sorting
        sortTimestamp: sortTimestampValue 
      };
      
      if (id) {
        // First, get the current blog data to check if date has changed
        const blogRef = doc(db, "blogs", id);
        const currentBlog = await getDoc(blogRef);
        const currentBlogData = currentBlog.data();
        
        // Store the original creation timestamp if not already stored
        if (!blogData.originalTimestamp && currentBlogData.timestamp) {
          blogData.originalTimestamp = currentBlogData.timestamp;
        }
        
        // Add currentTimestamp for update tracking
        blogData.updatedAt = serverTimestamp();
        
        if (currentBlogData.date !== date) {
          console.log(`Date changed from ${currentBlogData.date} to ${date}`);
          console.log(`Old sortTimestamp: ${currentBlogData.sortTimestamp}, New sortTimestamp: ${sortTimestampValue}`);
          toast.info("Blog date updated - post order will reflect the new date");
        }
        
        // Update existing blog
        await updateDoc(blogRef, blogData);
        
        // Force a refresh if date was changed
        if (currentBlogData.date !== date) {
          // Wait a bit for Firestore to update
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        
        toast.success("Blog updated successfully");
      } else {
        // For new blog posts
        blogData.timestamp = serverTimestamp();
        
        // Add new blog
        const docRef = await addDoc(collection(db, "blogs"), blogData);
        
        // Send notifications to subscribers for new blogs
        if (user.displayName) {
          const newBlog = { ...blogData, id: docRef.id };
          const notificationResult = await sendNewBlogNotification(newBlog, user.displayName);
          
          if (notificationResult?.success && notificationResult.subscriberCount > 0) {
            toast.info(`Notification sent to ${notificationResult.subscriberCount} subscribers`);
          }
        }
        
        toast.success("Blog created successfully");
      }
      
      setActive("home");
      navigate("/");
    } catch (err) {
      console.error(err);
      toast.error("Error submitting blog");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Function to insert image directly into the Quill editor
  const insertImage = (url, alignment) => {
    if (!quillRef.current) {
      console.error("Quill editor reference not available");
      return;
    }
    
    console.log("Inserting image:", url, "with alignment:", alignment);
    
    try {
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection(true);
      
      // Get current position
      const index = range ? range.index : 0;
      console.log("Inserting at position:", index);
      
      // Create image HTML with alignment and responsive behavior
      let imageHtml = '';
      if (alignment === 'left') {
        imageHtml = `<img src="${url}" alt="Blog image" class="responsive-image" style="float: left; margin: 0 15px 10px 0; max-width: 40%;" onerror="this.onerror=null; this.src='data:image/svg+xml,%3Csvg xmlns=\\'http://www.w3.org/2000/svg\\' width=\\'100\\' height=\\'100\\' viewBox=\\'0 0 100 100\\'%3E%3Crect width=\\'100\\' height=\\'100\\' fill=\\'%23f0f0f0\\'/%3E%3Ctext x=\\'50%25\\' y=\\'50%25\\' font-family=\\'Arial\\' font-size=\\'14\\' text-anchor=\\'middle\\' dy=\\'.3em\\' fill=\\'%23999\\'%3EImage failed to load%3C/text%3E%3C/svg%3E';" />`;
      } else if (alignment === 'right') {
        imageHtml = `<img src="${url}" alt="Blog image" class="responsive-image" style="float: right; margin: 0 0 10px 15px; max-width: 40%;" onerror="this.onerror=null; this.src='data:image/svg+xml,%3Csvg xmlns=\\'http://www.w3.org/2000/svg\\' width=\\'100\\' height=\\'100\\' viewBox=\\'0 0 100 100\\'%3E%3Crect width=\\'100\\' height=\\'100\\' fill=\\'%23f0f0f0\\'/%3E%3Ctext x=\\'50%25\\' y=\\'50%25\\' font-family=\\'Arial\\' font-size=\\'14\\' text-anchor=\\'middle\\' dy=\\'.3em\\' fill=\\'%23999\\'%3EImage failed to load%3C/text%3E%3C/svg%3E';" />`;
      } else {
        imageHtml = `<img src="${url}" alt="Blog image" class="responsive-image" style="display: block; margin: 10px auto; max-width: 80%;" onerror="this.onerror=null; this.src='data:image/svg+xml,%3Csvg xmlns=\\'http://www.w3.org/2000/svg\\' width=\\'100\\' height=\\'100\\' viewBox=\\'0 0 100 100\\'%3E%3Crect width=\\'100\\' height=\\'100\\' fill=\\'%23f0f0f0\\'/%3E%3Ctext x=\\'50%25\\' y=\\'50%25\\' font-family=\\'Arial\\' font-size=\\'14\\' text-anchor=\\'middle\\' dy=\\'.3em\\' fill=\\'%23999\\'%3EImage failed to load%3C/text%3E%3C/svg%3E';" />`;
      }
      
      console.log("Generated HTML:", imageHtml);
      
      // Insert the HTML at cursor position
      quill.clipboard.dangerouslyPasteHTML(index, imageHtml);
      
      // Also try the Quill built-in image insertion as a fallback
      // quill.insertEmbed(index, 'image', url);
      
      // Force a re-render of the editor content
      const currentContent = quill.getContents();
      setTimeout(() => {
        quill.setContents(currentContent);
        
        // Move cursor after the image
        quill.setSelection(index + 1);
      }, 10);
      
      // Log if image is in the editor content
      const editorContent = quill.root.innerHTML;
      console.log("Editor content contains image:", editorContent.includes(url));
      
      // Check image orientation and add appropriate class
      setTimeout(() => {
        const img = new Image();
        img.onload = function() {
          const isPortrait = this.height > this.width;
          
          // Find the inserted image in the editor
          const editorImages = document.querySelectorAll('.ql-editor img');
          console.log("Found images in editor:", editorImages.length);
          
          // Get the last inserted image
          const insertedImage = editorImages[editorImages.length - 1];
          
          if (insertedImage && isPortrait) {
            // Add portrait class or style
            if (alignment === 'center') {
              insertedImage.style.maxHeight = '500px';
              insertedImage.style.width = 'auto';
            }
          }
        };
        img.src = url;
      }, 100);
    } catch (error) {
      console.error("Error inserting image:", error);
      toast.error("Failed to insert image. Please try again.");
    }
  }
  
  // Handle image from URL
  const handleInsertImageUrl = () => {
    if (!imageUrl.trim()) {
      toast.error("Please enter a valid image URL");
      return;
    }
    
    insertImage(imageUrl, imageAlignment);
    
    // Ensure the form state is updated with the new content
    setTimeout(() => {
      const updatedContent = quillRef.current?.getEditor().root.innerHTML || description;
      console.log("Updated content after URL insertion:", updatedContent.substring(0, 50) + "...");
      setForm(prev => ({
        ...prev,
        description: updatedContent,
      }));
    }, 100);
    
    setShowImagePopup(false);
    setImageUrl("");
    setImageAlignment("center"); // Reset alignment to center after insertion
  };
  
  // Handle file selection for image upload
  const handleFileSelected = async (e) => {
    const selectedFile = e.target.files[0];
    
    if (selectedFile) {
      // Log file info for debugging
      console.log('File selected:', selectedFile.name, 'Type:', selectedFile.type, 'Size:', selectedFile.size);
      
      // Check file type
      const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];
      const fileType = selectedFile.type.toLowerCase();
      
      // Get file extension
      const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
      const isValidByExtension = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'].includes(fileExtension);
      
      if (validImageTypes.includes(fileType) || isValidByExtension) {
        // Check file size (10MB limit)
        if (selectedFile.size > 10 * 1024 * 1024) {
          toast.error('Image file is too large. Maximum size is 10MB.');
          return;
        }
        
        try {
          // Handle HEIC/HEIF format conversion
          if (fileExtension === 'heic' || fileExtension === 'heif' || 
              fileType === 'image/heic' || fileType === 'image/heif') {
            
            toast.info("Converting HEIC image to JPEG format...");
            
            try {
              // Convert HEIC to JPEG using heic2any
              const jpegBlob = await heic2any({
                blob: selectedFile,
                toType: "image/jpeg",
                quality: 0.8
              });
              
              // Create a new file from the blob
              const convertedFile = new File(
                [jpegBlob], 
                `${selectedFile.name.split('.')[0]}.jpg`, 
                { type: 'image/jpeg' }
              );
              
              console.log("HEIC file converted to JPEG successfully");
              toast.success("HEIC image converted successfully");
              
              // Use the converted file
              setFile(convertedFile);
            } catch (conversionError) {
              console.error("HEIC conversion failed:", conversionError);
              toast.error("Failed to convert HEIC image. Please try a different format.");
            }
          } else {
            // For non-HEIC files, use as is
            setFile(selectedFile);
          }
        } catch (error) {
          console.error("Error processing file:", error);
          toast.error("Error processing the selected file.");
        }
      } else {
        toast.error(`Unsupported file type. Please use JPG, PNG, GIF, WEBP, or HEIC format.`);
        console.error('Invalid file type:', fileType, 'Extension:', fileExtension);
      }
    }
  };
  
  // Open file browser with support for mobile formats
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  if (isLoading) {
    return <Spinner />;
  }

  return (
   <div className="container-fluid mb-4">
      {isSubmitting && <Spinner />}
    <div className="container">
      <div className="col-12">
        <div className="text-center heading py-2">
    {id ? "Update Blog" : "Create Blog"}
        </div>
      </div>
      <div className="row h-100 justify-content-center align-items-center">
        <div className="col-12 col-md-10 col-lg-8">
          <form className="row blog-form" onSubmit={handleSubmit}>
            <div className="col-12 py-3">
                <input 
                  type="text"
              className="form-control input-text-box"
                  placeholder="Title *"
              name="title"
              value={title}
                  onChange={handleChange}
                />
            </div>
              
            <div className="col-12 py-3">
                <input 
                  type="date"
                  className="form-control input-text-box"
                  placeholder="Date *"
                  name="date"
                  value={date}
                  onChange={handleChange}
                />
            </div>
              
            <div className="col-12 py-3">
                <div className="editor-toolbar">
                  <button 
                    type="button" 
                    className="toolbar-btn" 
                    title="Insert Image from URL"
                    onClick={() => setShowImagePopup(true)}
                  >
                    🖼️
                  </button>
                  <button 
                    type="button" 
                    className="toolbar-btn" 
                    title="Upload Image (supports HEIC)"
                    onClick={triggerFileInput}
                  >
                    📤
                  </button>
                  <div className="toolbar-separator"></div>
                  <button 
                    type="button" 
                    className={`toolbar-btn ${pinned ? 'active-pin' : ''}`}
                    title={pinned ? "Unpin Post" : "Pin Post to Top"}
                    onClick={togglePin}
                  >
                    📌
                  </button>
                  <div className="toolbar-separator"></div>
                  <div className="emoji-tag-toolbar">
                    <button 
                      type="button" 
                      className="toolbar-btn emoji-btn" 
                      title="Add Emoji Tag"
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    >
                      {emojiTag || '😀'}
                    </button>
                    {showEmojiPicker && (
                      <div className="emoji-picker toolbar-popup">
                        {EMOJI_OPTIONS.map((option) => (
                          <div 
                            key={option.emoji} 
                            className="emoji-option" 
                            onClick={() => handleEmojiSelect(option.emoji)}
                            title={option.label}
                          >
                            {option.emoji}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Hidden file input for image uploads */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelected}
                  style={{ display: "none" }}
                  accept="image/*,.heic,.HEIC"
                />
                
                {file && progress !== null && progress < 100 && (
                  <div className="progress mt-2">
                    <div 
                      className="progress-bar progress-bar-striped progress-bar-animated" 
                      role="progressbar" 
                      style={{ width: `${progress}%` }} 
                      aria-valuenow={progress} 
                      aria-valuemin="0" 
                      aria-valuemax="100"
                    >
                      {`${progress}%`}
                    </div>
                  </div>
                )}

                {/* IMPORTANT: Keep video and link buttons in toolbar */}
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  modules={quillModules}
                  formats={quillFormats}
                  value={description}
                  onChange={handleEditorChange}
                  placeholder="Write your blog content..."
                  className="quill-editor"
                />
              </div>
              
              <div className="col-12 py-3 text-center">
                <button 
                  className={`btn btn-add ${isSubmitting ? 'submitting' : ''}`}
                  type="submit"
                  disabled={isSubmitting || (progress !== null && progress < 100)}
                >
                  {isSubmitting ? 'Saving...' : (id ? 'Update' : 'Submit')}
                </button>
              </div>
            </form>
          </div>
               </div>
            </div>
      
      {/* Image Upload Popup */}
      {showImagePopup && (
        <div className="image-popup" style={{
          position: 'fixed', 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -50%)',
          zIndex: 1050,
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          width: '90%',
          maxWidth: '500px'
        }}>
          <div className="image-popup-content">
            <h5>Insert Image</h5>
            <div className="mb-3">
              <label htmlFor="imageUrl" className="form-label">Image URL</label>
              <input 
                type="text" 
                className="form-control" 
                id="imageUrl"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
              />
            </div>
            
            <div className="mb-3">
              <label className="form-label">Alignment</label>
              <div className="alignment-options">
                <div className="form-check form-check-inline">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="imageAlignment"
                    id="alignLeft"
                    value="left"
                    checked={imageAlignment === "left"}
                    onChange={() => setImageAlignment("left")}
                  />
                  <label className="form-check-label" htmlFor="alignLeft">
                    <div className="alignment-icon align-left-icon"></div>
                  </label>
                </div>
                <div className="form-check form-check-inline">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="imageAlignment"
                    id="alignCenter"
                    value="center"
                    checked={imageAlignment === "center"}
                    onChange={() => setImageAlignment("center")}
                  />
                  <label className="form-check-label" htmlFor="alignCenter">
                    <div className="alignment-icon align-center-icon"></div>
                  </label>
                </div>
                <div className="form-check form-check-inline">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="imageAlignment"
                    id="alignRight"
                    value="right"
                    checked={imageAlignment === "right"}
                    onChange={() => setImageAlignment("right")}
                  />
                  <label className="form-check-label" htmlFor="alignRight">
                    <div className="alignment-icon align-right-icon"></div>
                  </label>
                </div>
              </div>
            </div>
            
            <div className="popup-actions">
              <button 
                type="button" 
                className="btn btn-outline-secondary me-2"
                onClick={() => setShowImagePopup(false)}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-primary"
                onClick={handleInsertImageUrl}
              >
                Insert
              </button>
            </div>
          </div>
        </div>
      )}
   </div>
  )
}

export default AddEditBlog